import { getEnvironmentConfig } from "@/config/environment";

export interface AnalysisInsight {
  analysis_text: string;
  analysis_type: string;
  business_unit_id: number;
  data_summary: Record<string, any>;
  importance: 'low' | 'medium' | 'high';
  original_query: string;
  recommendations: string[];
  tags: string[];
}

export interface AnalysisInsightResponse {
  id: string;
  business_unit_id: number;
  analysis_text: string;
  original_query: string;
  timestamp: string;
  analysis_type: string;
  data_summary: Record<string, any>;
  recommendations: string[];
  visualizations: any;
  sql_query: string | null;
  result_count: number | null;
  user_id: string | null;
  tags: string[];
  notes: string | null;
  importance: string;
  requires_action: boolean;
  action_taken: string | null;
  related_insight_ids: string[];
}

export interface GetAnalysisInsightsParams {
  business_unit_id: number;
  limit?: number;
  offset?: number;
  analysis_type?: string;
  importance?: string;
  requires_action?: boolean;
  search?: string;
}

class AnalysisInsightsApiService {
  private readonly baseUrl: string;

  constructor() {
    const config = getEnvironmentConfig();
    this.baseUrl = config.apiUrl;
  }

  async saveAnalysisInsight(insight: AnalysisInsight): Promise<AnalysisInsightResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/analysis-insights/`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'accept': 'application/json',
            'content-type': 'application/json',
            'ngrok-skip-browser-warning': '1',
            'origin': window.location.origin
          },
          body: JSON.stringify(insight)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save analysis insight: ${response.statusText}`);
      }

      const data = await response.json();
      return data as AnalysisInsightResponse;
    } catch (error) {
      console.error('Error saving analysis insight:', error);
      throw error;
    }
  }

  async getAnalysisInsights(params: GetAnalysisInsightsParams): Promise<AnalysisInsightResponse[]> {
    try {
      const { 
        business_unit_id, 
        limit = 50, 
        offset = 0, 
        analysis_type, 
        importance, 
        requires_action, 
        search 
      } = params;

      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (analysis_type) {
        queryParams.append('analysis_type', analysis_type);
      }
      if (importance) {
        queryParams.append('importance', importance);
      }
      if (requires_action !== undefined) {
        queryParams.append('requires_action', requires_action.toString());
      }
      if (search) {
        queryParams.append('search', search);
      }

      const response = await fetch(
        `${this.baseUrl}/api/v1/analysis-insights/business-unit/${business_unit_id}?${queryParams.toString()}`,
        {
          mode: 'cors',
          headers: {
            'accept': 'application/json',
            'ngrok-skip-browser-warning': '1',
            'origin': window.location.origin
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch analysis insights: ${response.statusText}`);
      }

      const data = await response.json();
      return data as AnalysisInsightResponse[];
    } catch (error) {
      console.error('Error fetching analysis insights:', error);
      throw error;
    }
  }

  async getAnalysisInsightById(insightId: string): Promise<AnalysisInsightResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/analysis-insights/${insightId}`,
        {
          mode: 'cors',
          headers: {
            'accept': 'application/json',
            'ngrok-skip-browser-warning': '1',
            'origin': window.location.origin
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch analysis insight: ${response.statusText}`);
      }

      const data = await response.json();
      return data as AnalysisInsightResponse;
    } catch (error) {
      console.error('Error fetching analysis insight:', error);
      throw error;
    }
  }
}

export const analysisInsightsApiService = new AnalysisInsightsApiService();